// How each weapon is held, carried, stowed and reloaded. The cowboy view (index.ts) places the gun from these
// numbers and IKs the hands onto it, so a gun's hold follows the weapon in hand, not the class it was baked on.
//
// Frames: carry / reload grips are in the body frame (x right, y up, z forward, metres from the feet, for a
// 1.9 m cowboy; scaled by height). Gun-local points (foregrip, mag well, breech) are in the gun's own frame:
// origin at the grip, barrel down +Z, top +Y. `fore` is the support-hand grip as a fraction of the muzzle length.

import { W, weaponDef } from '../../../sim/weapons';
import { TICK_RATE } from '../../../sim/core';

export const enum HoldK { Pistol = 0, Akimbo, SawedOff, Rifle, Shotgun, Sniper, Heavy, Launcher, Lob, Heave }
export const enum StowK { Thigh = 0, Back = 1 }
export const enum ReloadK { Mag = 0, Revolver, Break, RifleMag, Bolt, Akimbo, Tube, Drum, Belt, Vial, Flick }

export interface Pose { x: number; y: number; z: number; pitch: number; yaw: number; roll: number }

export interface Hold {
  k: HoldK;
  /** support hand on the gun (foregrip IK) */
  twoHand: boolean;
  /** aim from the pistol idle clip (one arm out) rather than the rifle idle */
  pistolAim: boolean;
  stow: StowK;
  reload: ReloadK;
  fore: number;
  /** ADS: distance in front of the eye, right offset, drop below the eye */
  ads: [number, number, number];
  /** quiet-gun carry while moving / standing */
  carry: Pose;
  /** sprint carry (port arms for long guns) */
  sprint: Pose;
}

const P = (x: number, y: number, z: number, pitch: number, yaw = 0, roll = 0): Pose => ({ x, y, z, pitch, yaw, roll });

const HOLDS: Record<HoldK, Hold> = {
  // level and out front: pistols are carried horizontal at low ready, never dangled
  [HoldK.Pistol]: { k: HoldK.Pistol, twoHand: false, pistolAim: true, stow: StowK.Thigh, reload: ReloadK.Mag, fore: 0,
    ads: [0.5, 0.03, 0.06], carry: P(0.2, 1.02, 0.3, -0.12, -0.08), sprint: P(0.22, 1.08, 0.18, 0.15, -0.1) },
  [HoldK.Akimbo]: { k: HoldK.Akimbo, twoHand: false, pistolAim: true, stow: StowK.Thigh, reload: ReloadK.Akimbo, fore: 0,
    ads: [0.5, 0.06, 0.08], carry: P(0.2, 1.0, 0.28, -0.15, -0.05), sprint: P(0.22, 1.05, 0.15, 0.15, -0.05) },
  [HoldK.SawedOff]: { k: HoldK.SawedOff, twoHand: true, pistolAim: false, stow: StowK.Thigh, reload: ReloadK.Break, fore: 0.5,
    ads: [0.38, 0.08, 0.1], carry: P(0.18, 1.02, 0.16, -0.3, -0.2), sprint: P(0.15, 1.05, 0.14, 0.5, -0.7) },
  [HoldK.Rifle]: { k: HoldK.Rifle, twoHand: true, pistolAim: false, stow: StowK.Back, reload: ReloadK.RifleMag, fore: 0.48,
    ads: [0.32, 0.15, 0.13], carry: P(0.22, 1.18, 0.2, -0.32, -0.18, 0.2), sprint: P(0.13, 1.02, 0.12, 0.75, -0.95) },
  [HoldK.Shotgun]: { k: HoldK.Shotgun, twoHand: true, pistolAim: false, stow: StowK.Back, reload: ReloadK.Break, fore: 0.45,
    ads: [0.32, 0.15, 0.13], carry: P(0.22, 1.18, 0.2, -0.32, -0.18, 0.2), sprint: P(0.13, 1.02, 0.12, 0.75, -0.95) },
  [HoldK.Sniper]: { k: HoldK.Sniper, twoHand: true, pistolAim: false, stow: StowK.Back, reload: ReloadK.Bolt, fore: 0.42,
    ads: [0.3, 0.15, 0.12], carry: P(0.22, 1.2, 0.18, -0.38, -0.18, 0.2), sprint: P(0.13, 1.02, 0.1, 0.8, -0.95) },
  // the Auto MG rides the hip, both hands on it, muzzle level
  [HoldK.Heavy]: { k: HoldK.Heavy, twoHand: true, pistolAim: false, stow: StowK.Back, reload: ReloadK.RifleMag, fore: 0.38,
    ads: [0.34, 0.12, 0.16], carry: P(0.22, 1.02, 0.06, -0.1, -0.12), sprint: P(0.15, 1.0, 0.12, 0.6, -0.9) },
  // launchers sit on the right shoulder, tube forward, support hand out on the tube
  [HoldK.Launcher]: { k: HoldK.Launcher, twoHand: true, pistolAim: false, stow: StowK.Back, reload: ReloadK.Tube, fore: 0.32,
    ads: [0.22, 0.16, 0.12], carry: P(0.2, 1.3, 0.1, 0.2, -0.05), sprint: P(0.2, 1.3, 0.05, 0.45, -0.05) },
  // beacons and canisters: held in one hand like a pistol, ready to lob
  [HoldK.Lob]: { k: HoldK.Lob, twoHand: false, pistolAim: true, stow: StowK.Thigh, reload: ReloadK.Mag, fore: 0,
    ads: [0.45, 0.05, 0.08], carry: P(0.2, 1.0, 0.25, -0.2, -0.05), sprint: P(0.22, 1.05, 0.15, 0.1, -0.05) },
  // the giant ground fireworks: hugged at the hip in both arms, nose forward, ready to heave
  [HoldK.Heave]: { k: HoldK.Heave, twoHand: true, pistolAim: false, stow: StowK.Back, reload: ReloadK.Tube, fore: 0.5,
    ads: [0.4, 0.12, 0.26], carry: P(0.16, 0.98, 0.16, -0.05, -0.12), sprint: P(0.16, 1.0, 0.14, 0.25, -0.5) },
};

export function holdKind(weapon: number | undefined, classPistol: boolean): HoldK {
  if (weapon === undefined || weapon < 0 || weapon > 200) return classPistol ? HoldK.Pistol : HoldK.Rifle;
  switch (weapon) {
    case W.Starter: case W.BigIron: case W.DuelBlaster: return HoldK.Pistol;
    case W.Akimbo: return HoldK.Akimbo;
    case W.Scoundrel: return HoldK.SawedOff;
    case W.PulseRifle: return HoldK.Rifle;
    case W.AutoMG: return HoldK.Heavy;
    case W.BoltSniper: return HoldK.Sniper;
    case W.Gentlemen: return HoldK.Shotgun;
    case W.Rocket: case W.RocketFire: case W.RocketIce: case W.RocketShock: case W.RocketAcid: case W.RocketMag:
    case W.BadIdea: return HoldK.Launcher;
    case W.Harpoon: return HoldK.Rifle;
    case W.BigOne: case W.Turret: return HoldK.Lob;
    default: return HoldK.Heave;
  }
}

export function holdOf(weapon: number | undefined, classPistol: boolean): Hold {
  const h = HOLDS[holdKind(weapon, classPistol)];
  return (weapon !== undefined && PER_GUN.get(weapon)) || h;
}
// each gun's own reload and where the support hand closes on it (fraction of the muzzle length:
// the handguard, the front grip, the forend under the hinge). Timings match gunmodels.ts.
const PER_GUN = new Map<number, Hold>([
  [W.BigIron, { ...HOLDS[HoldK.Pistol], reload: ReloadK.Revolver }],
  [W.DuelBlaster, { ...HOLDS[HoldK.Pistol], reload: ReloadK.Vial }],
  [W.Scoundrel, { ...HOLDS[HoldK.SawedOff], reload: ReloadK.Flick, fore: 0.5 }],
  [W.PulseRifle, { ...HOLDS[HoldK.Rifle], reload: ReloadK.Drum, fore: 0.57 }],
  [W.AutoMG, { ...HOLDS[HoldK.Heavy], reload: ReloadK.Belt, fore: 0.38 }],
  [W.Gentlemen, { ...HOLDS[HoldK.Shotgun], fore: 0.36 }],
  [W.Harpoon, { ...HOLDS[HoldK.Rifle], fore: 0.42 }],
  [W.BadIdea, { ...HOLDS[HoldK.Launcher], fore: 0.35 }],
  ...[W.Rocket, W.RocketFire, W.RocketIce, W.RocketShock, W.RocketAcid, W.RocketMag].map((w) => [w, { ...HOLDS[HoldK.Launcher], fore: 0.47 }] as [number, Hold]),
  ...[W.EmberCanopy, W.FrostCanopy, W.StormCanopy, W.CausticCanopy, W.MagnetCanopy, W.AegisCanopy, W.CoolantRain, W.MendingRain].map((w) => [w, { ...HOLDS[HoldK.Heave], fore: 0.12 }] as [number, Hold]),
]);

/** seconds of one reload cycle (a whole mag, or one round for round-by-round loaders) */
export function reloadCycle(weapon: number | undefined, classPistol: boolean): number {
  if (weapon === undefined || weapon < 0 || weapon > 200) return classPistol ? 0.45 : 1.5;
  const d = weaponDef(weapon);
  return Math.max(0.25, d.reloadTicks / TICK_RATE);
}

// ---------------------------------------------------------------- reload choreography
// Each kind returns the gun's body-frame pose and the support hand's target for a cycle phase u (0..1).
// The support hand target is either gun-local (`handGun` true: a point on the gun) or body-frame (belt, pouch).

export interface ReloadFrame { gun: Pose; hand: { x: number; y: number; z: number }; handGun: boolean }

const smooth = (t: number) => { const c = Math.min(1, Math.max(0, t)); return c * c * (3 - 2 * c); };
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
type V3 = [number, number, number];
function path(u: number, keys: [number, V3][]): V3 {
  if (u <= keys[0][0]) return keys[0][1];
  for (let i = 1; i < keys.length; i++) {
    if (u <= keys[i][0]) {
      const [t0, a] = keys[i - 1], [t1, b] = keys[i];
      const s = smooth((u - t0) / (t1 - t0));
      return [lerp(a[0], b[0], s), lerp(a[1], b[1], s), lerp(a[2], b[2], s)];
    }
  }
  return keys[keys.length - 1][1];
}
const out: ReloadFrame = { gun: P(0, 0, 0, 0), hand: { x: 0, y: 0, z: 0 }, handGun: false };
function set(g: Pose, hand: V3, handGun: boolean): ReloadFrame {
  out.gun = g; out.hand.x = hand[0]; out.hand.y = hand[1]; out.hand.z = hand[2]; out.handGun = handGun; return out;
}

/**
 * The reload pose at phase u of one cycle. `muz` is the held gun's muzzle length (m), for points along the gun.
 * Mags: tilt the gun, strip the mag to the belt, seat a fresh one with a slap, charge it. Revolver: cylinder out,
 * thumb a round in per cycle. Break actions: muzzle drops (broken open), a shell to the breech, snap shut.
 */
export function reloadFrame(k: ReloadK, u: number, muz: number): ReloadFrame {
  switch (k) {
    case ReloadK.Mag: {
      const jolt = u > 0.62 && u < 0.72 ? Math.sin((u - 0.62) / 0.1 * Math.PI) * 0.12 : 0;
      const g = P(0.12, 1.12 + jolt * 0.2, 0.3, 0.25 + jolt, -0.25, -0.45);
      if (u < 0.15 || u > 0.62) return set(g, u < 0.15 ? [0, -0.1, 0] : path(u, [[0.62, [0, -0.12, -0.01]], [0.75, [0, -0.1, -0.02]], [1, [-0.02, -0.06, 0.03]]]), true);
      return set(g, path(u, [[0.15, [-0.04, 1.0, 0.12]], [0.3, [-0.12, 0.96, 0.05]], [0.45, [-0.06, 1.0, 0.16]], [0.62, [0.08, 1.04, 0.28]]]), false);
    }
    case ReloadK.Revolver: {
      // per round: belt pouch -> cylinder, thumb it home
      const g = P(0.1, 1.1, 0.3, 0.55, -0.3, -0.9);
      if (u < 0.45) return set(g, path(u, [[0, [0.02, 1.08, 0.3]], [0.2, [-0.1, 0.97, 0.06]], [0.45, [0.04, 1.06, 0.26]]]), false);
      return set(g, path(u, [[0.45, [0.04, 0.06, 0.05]], [0.7, [0.03, 0.07, 0.07]], [1, [0.03, 0.08, 0.04]]]), true);
    }
    case ReloadK.Break: {
      // broken open, muzzle down, held up where the breech is visible; a shell from the belt into the breech
      // (dips a little as it goes in). Round-by-round guns repeat this, so it stays open across cycles.
      const g = P(0.15, 1.12, 0.3, -0.5 + Math.sin(Math.PI * u) * 0.12, -0.3, -0.2);
      if (u < 0.5) return set(g, path(u, [[0, [-0.02, 1.0, 0.3]], [0.22, [-0.12, 0.95, 0.06]], [0.5, [0.06, 1.08, 0.24]]]), false);
      return set(g, path(u, [[0.5, [0, 0.08, 0.02]], [0.75, [0, 0.06, 0.05]], [1, [0, -0.02, muz * 0.45]]]), true);
    }
    case ReloadK.Bolt: {
      // bolt gun stays shouldered-ish and canted; per round: belt -> into the open breech, then back on the stock
      const g = P(0.14, 1.15, 0.22, 0.2, -0.3, -0.5);
      if (u < 0.6) return set(g, path(u, [[0, [0.02, 1.08, 0.28]], [0.3, [-0.1, 0.97, 0.06]], [0.6, [0.06, 1.14, 0.3]]]), false);
      return set(g, path(u, [[0.6, [0.03, 0.05, muz * 0.12]], [0.75, [0.03, 0.05, muz * 0.12]], [1, [0, -0.04, muz * 0.42]]]), true);
    }
    case ReloadK.RifleMag: {
      // gun pulled in and canted, muzzle up: the mag well turns toward the support hand; a jolt as the mag seats
      const seat = u > 0.62 && u < 0.72 ? Math.sin((u - 0.62) / 0.1 * Math.PI) : 0;
      const g = P(0.12, 1.12 + seat * 0.02, 0.18, 0.38 + seat * 0.1, -0.5, -0.75);
      const well: V3 = [0, -0.12, muz * 0.22];
      if (u < 0.12) return set(g, [0, -0.04, muz * 0.45], true);
      if (u < 0.55) return set(g, path(u, [[0.12, [0.0, 1.06, 0.3]], [0.3, [-0.16, 0.98, 0.0]], [0.55, [0.02, 1.02, 0.28]]]), false);
      // seat, slap, then rack the charging handle / work the bolt, and back to the foregrip
      return set(g, path(u, [[0.55, well], [0.68, [0, -0.1, muz * 0.2]], [0.8, [0.05, 0.07, muz * 0.12]], [0.88, [0.06, 0.07, -0.02]], [1, [0, -0.04, muz * 0.45]]]), true);
    }
    case ReloadK.Akimbo: {
      // both guns flick muzzle-up to dump the mags, drop to the hips for fresh ones, snap back level
      const up = smooth(u / 0.15) * (1 - smooth((u - 0.3) / 0.1));
      const low = smooth((u - 0.3) / 0.12) * (1 - smooth((u - 0.75) / 0.15));
      return set(P(0.2, 1.05 - low * 0.12 + up * 0.08, 0.25 - low * 0.12, -0.12 + up * 1.2 - low * 0.9, -0.05), [0, 0, 0], true);
    }
    case ReloadK.Drum: {
      // Pulse Rifle: canted so the drum hangs toward the support hand; it swings down off its hinge
      // and drops, a fresh one from the belt swings up and clicks, then a tap on the cells
      const seat = u > 0.6 && u < 0.7 ? Math.sin((u - 0.6) / 0.1 * Math.PI) : 0;
      const g = P(0.12, 1.12 + seat * 0.02, 0.2, 0.3 + seat * 0.08, -0.45, -0.6);
      if (u < 0.36) return set(g, path(u, [[0, [0, -0.04, muz * 0.57]], [0.14, [0, -0.1, 0.14]], [0.24, [0, -0.12, 0.17]], [0.36, [0, -0.2, 0.17]]]), true);
      if (u < 0.52) return set(g, path(u, [[0.36, [0.02, 1.0, 0.24]], [0.44, [-0.14, 0.97, 0.05]], [0.52, [0.02, 1.02, 0.26]]]), false);
      return set(g, path(u, [[0.52, [0, -0.17, 0.14]], [0.68, [0, -0.1, 0.14]], [0.78, [0, 0.17, 0.24]], [0.86, [0, 0.17, 0.26]], [1, [0, -0.04, muz * 0.57]]]), true);
    }
    case ReloadK.Belt: {
      // Auto MG at the hip: feed cover up, the drum off the left side, a fresh one on, cover down, crank
      const g = P(0.16, 1.06, 0.14, 0.12, -0.4, 0.5);
      if (u < 0.38) return set(g, path(u, [[0, [0, -0.04, muz * 0.38]], [0.12, [0, 0.16, 0.06]], [0.2, [0, 0.19, 0.04]], [0.28, [-0.16, 0.04, 0.12]], [0.38, [-0.2, -0.04, 0.12]]]), true);
      if (u < 0.56) return set(g, path(u, [[0.38, [-0.02, 0.98, 0.18]], [0.47, [-0.15, 0.96, 0.02]], [0.56, [-0.04, 1.02, 0.18]]]), false);
      return set(g, path(u, [[0.56, [-0.2, 0.0, 0.12]], [0.68, [-0.16, 0.04, 0.12]], [0.74, [0, 0.19, 0.06]], [0.8, [0, 0.15, 0.04]], [0.86, [0.09, 0.1, 0.05]], [0.92, [0.09, 0.1, -0.02]], [1, [0, -0.04, muz * 0.38]]]), true);
    }
    case ReloadK.Vial: {
      // Duel Blaster: muzzle up, flip the lid, pluck the spent vial, drop a fresh one in, lid shut
      const g = P(0.12, 1.14, 0.3, 0.45, -0.25, -0.3);
      if (u < 0.32) return set(g, path(u, [[0, [0, -0.1, 0]], [0.1, [0, 0.12, 0.12]], [0.18, [0, 0.13, 0.08]], [0.32, [0.02, 0.16, 0.06]]]), true);
      if (u < 0.56) return set(g, path(u, [[0.32, [0.02, 1.16, 0.3]], [0.44, [-0.1, 0.97, 0.06]], [0.56, [0.02, 1.14, 0.3]]]), false);
      return set(g, path(u, [[0.56, [0, 0.16, 0.075]], [0.7, [0, 0.12, 0.075]], [0.78, [0, 0.12, 0.12]], [0.86, [0, 0.1, 0.1]], [1, [0, -0.1, 0]]]), true);
    }
    case ReloadK.Flick: {
      // The Scoundrel, one-handed: a wrist flick drops the barrels open (the shells fly), a fresh
      // shell from the belt, then a flick back up snaps it shut
      const down = smooth(u / 0.1) * (1 - smooth((u - 0.72) / 0.08));
      const snap = u > 0.74 && u < 0.92 ? Math.sin((u - 0.74) / 0.18 * Math.PI) * 0.35 : 0;
      const g = P(0.16, 1.1, 0.3, -0.15 - down * 0.85 + snap, -0.25, -0.2);
      if (u < 0.12) return set(g, [0, -0.1, 0.02], true);
      if (u < 0.46) return set(g, path(u, [[0.12, [0.02, 1.05, 0.3]], [0.28, [-0.12, 0.96, 0.06]], [0.46, [0.04, 1.08, 0.3]]]), false);
      return set(g, path(u, [[0.46, [0, 0.11, 0.0]], [0.62, [0, 0.08, 0.07]], [0.72, [0, 0.07, 0.08]], [1, [0, -0.04, muz * 0.5]]]), true);
    }
    case ReloadK.Tube: default: {
      // launcher: off the shoulder, a fresh round down the front of the tube, back up
      const g = P(0.18, 1.1, 0.2, 0.4, -0.3, -0.2);
      if (u < 0.15) return set(g, [0, -0.02, muz * 0.32], true);
      if (u < 0.55) return set(g, path(u, [[0.15, [0.0, 1.12, 0.4]], [0.32, [-0.14, 1.0, 0.06]], [0.55, [0.12, 1.2, 0.2 + muz]]]), false);
      return set(g, path(u, [[0.55, [0.02, 0.06, muz * 1.0]], [0.8, [0.02, 0.02, muz * 0.75]], [1, [0, -0.02, muz * 0.32]]]), true);
    }
  }
}
